import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { PortalNav } from "@/components/PortalNav";
import { RepPicker, isElevatedRole } from "@/components/RepPicker";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BarChart3, Loader2, ChevronRight, X, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const TYPE_COLORS = { "Phone Call": "#0F172A", "Email": "#2563EB", "In Person Visit": "#D4AF37", "Scheduled Visit": "#10B981" };
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const RANGE_DEFAULT = { day: 13, week: 83, month: 364 };

function presetRange(key) {
  const t = new Date();
  const y = t.getFullYear(), m = t.getMonth(), q = Math.floor(m / 3) * 3;
  switch (key) {
    case "wtd": { const d = new Date(t); d.setDate(t.getDate() - ((t.getDay() + 6) % 7)); return [iso(d), iso(t), "day"]; }
    case "mtd": return [iso(new Date(y, m, 1)), iso(t), "day"];
    case "qtd": return [iso(new Date(y, q, 1)), iso(t), "week"];
    case "prev_month": return [iso(new Date(y, m - 1, 1)), iso(new Date(y, m, 0)), "day"];
    default: return [iso(new Date(y, q - 3, 1)), iso(new Date(y, q, 0)), "week"];
  }
}
const PRESETS = [["wtd", "WTD"], ["mtd", "MTD"], ["qtd", "QTD"], ["prev_month", "Previous Month"], ["prev_quarter", "Previous Quarter"]];

export default function CrmActivityReport() {
  const { user } = useAuth();
  const [group, setGroup] = useState("day");
  const [start, setStart] = useState(daysAgo(13));
  const [end, setEnd] = useState(iso(new Date()));
  const [repId, setRepId] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);
  const [preset, setPreset] = useState(null);
  const [exporting, setExporting] = useState(false);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const params = { start, end, group };
      if (repId !== "all") params.rep_id = repId;
      const { data: blob } = await api.get("/crm/activity-report/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `activity_report_${start}_to_${end}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Could not export the report");
    } finally { setExporting(false); }
  };

  useEffect(() => {
    setLoading(true);
    const params = { start, end, group };
    if (repId !== "all") params.rep_id = repId;
    api.get("/crm/activity-report", { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load report"))
      .finally(() => setLoading(false));
  }, [start, end, group, repId]);

  const pickGroup = (g) => { setGroup(g); setStart(daysAgo(RANGE_DEFAULT[g])); setEnd(iso(new Date())); setPreset(null); };
  const pickPreset = (k) => { const [s, e, g] = presetRange(k); setStart(s); setEnd(e); setGroup(g); setPreset(k); };
  const setStartManual = (v) => { setStart(v); setPreset(null); };
  const setEndManual = (v) => { setEnd(v); setPreset(null); };

  return (
    <div className="min-h-screen bg-[#F4F4F5] md:pl-[var(--sb-w,16rem)]">
      <PortalNav />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-10" data-testid="activity-report-page">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-2">CRM Reporting</p>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-[#0F172A] flex items-center gap-3">
              <BarChart3 className="h-8 w-8 text-[#D4AF37]" /> Activity Report
            </h1>
            <button onClick={exportXlsx} disabled={exporting || loading} data-testid="activity-export-btn"
              className="flex items-center gap-2 bg-[#0F172A] text-white px-4 py-2.5 text-sm font-medium hover:bg-[#1e293b] disabled:opacity-60">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-[#D4AF37]" />} Export to Excel
            </button>
          </div>
          <p className="mt-2 text-sm text-zinc-500">
            {isElevatedRole(user?.role) ? "Calls and visits logged by each salesman." : "Your logged calls and visits."}
          </p>
          <Filters group={group} pickGroup={setGroup} resetGroup={pickGroup} preset={preset} pickPreset={pickPreset} start={start} setStart={setStartManual} end={end} setEnd={setEndManual} repId={repId} setRepId={setRepId} />
          {loading || !data ? (
            <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0F172A]" /></div>
          ) : (
            <>
              <Kpis data={data} />
              <TrendChart data={data} />
              <RepTable data={data} onDrill={setDrill} />
            </>
          )}
        </motion.div>
      </main>
      {drill && data && <DrillModal rep={drill} data={data} onClose={() => setDrill(null)} />}
    </div>
  );
}

function Filters({ group, pickGroup, resetGroup, preset, pickPreset, start, setStart, end, setEnd, repId, setRepId }) {
  const inp = "px-3 py-2 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]";
  return (
    <div className="mt-6 space-y-3" data-testid="activity-filters">
      <div className="flex flex-wrap gap-2" data-testid="activity-presets">
        {PRESETS.map(([k, l]) => (
          <button key={k} onClick={() => pickPreset(k)} data-testid={`activity-preset-${k}`}
            className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wide border transition-colors ${preset === k ? "bg-[#D4AF37] border-[#D4AF37] text-[#0F172A]" : "bg-white border-zinc-300 text-zinc-600 hover:border-[#0F172A]"}`}>{l}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3">
      <div className="flex border border-zinc-300 bg-white">
        {["day", "week", "month"].map((g) => (
          <button key={g} onClick={() => (preset ? pickGroup(g) : resetGroup(g))} data-testid={`activity-group-${g}`}
            className={`px-4 py-2 text-sm capitalize transition-colors ${group === g ? "bg-[#0F172A] text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>{g}</button>
        ))}
      </div>
      <label className="text-xs text-zinc-500">From<input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="activity-start" className={`${inp} block mt-1`} /></label>
      <label className="text-xs text-zinc-500">To<input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="activity-end" className={`${inp} block mt-1`} /></label>
      <RepPicker value={repId} onChange={setRepId} testid="activity-rep-picker" />
      </div>
    </div>
  );
}

function Kpis({ data }) {
  const byType = (t) => data.reps.reduce((a, r) => a + (r.by_type[t] || 0), 0);
  const periods = data.series.length || 1;
  const cards = [
    { label: "Total actions", value: data.total, testid: "kpi-total" },
    ...data.event_types.map((t) => ({ label: t, value: byType(t), testid: `kpi-${t.toLowerCase().replace(/ /g, "-")}` })),
    { label: `Avg per ${data.group}`, value: (data.total / periods).toFixed(1), testid: "kpi-avg" },
  ];
  return (
    <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {cards.map((c) => (
        <div key={c.label} className="bg-white border border-zinc-200 p-4" data-testid={c.testid}>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400">{c.label}</div>
          <div className="font-display font-extrabold text-2xl text-[#0F172A] mt-1">{c.value}</div>
        </div>
      ))}
    </div>
  );
}

function TrendChart({ data }) {
  return (
    <div className="mt-6 bg-white border border-zinc-200 p-4" data-testid="activity-trend-chart">
      <h2 className="font-display font-bold text-[#0F172A] mb-3">Trend by {data.group}</h2>
      <div style={{ height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data.series}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
            <XAxis dataKey="period" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={30} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {data.event_types.map((t) => <Bar key={t} dataKey={t} stackId="a" fill={TYPE_COLORS[t]} />)}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function RepTable({ data, onDrill }) {
  return (
    <div className="mt-6 bg-white border border-zinc-200 overflow-x-auto" data-testid="activity-rep-table">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-[11px] uppercase tracking-wide text-zinc-500">
          <tr>
            <th className="text-left px-4 py-3">Salesman</th>
            {data.event_types.map((t) => <th key={t} className="text-right px-4 py-3">{t}</th>)}
            <th className="text-right px-4 py-3">Total</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {data.reps.length === 0 && (
            <tr><td colSpan={data.event_types.length + 3} className="px-4 py-12 text-center text-zinc-400" data-testid="activity-empty">No activity logged in this range.</td></tr>
          )}
          {data.reps.map((r) => (
            <tr key={r.rep_id} className="hover:bg-zinc-50 cursor-pointer" onClick={() => onDrill(r)} data-testid={`activity-rep-row-${r.rep_id}`}>
              <td className="px-4 py-3 font-medium text-[#0F172A]">{r.rep_name}</td>
              {data.event_types.map((t) => <td key={t} className="px-4 py-3 text-right font-mono">{r.by_type[t] || 0}</td>)}
              <td className="px-4 py-3 text-right font-mono font-bold text-[#B8860B]">{r.total}</td>
              <td className="px-4 py-3 text-zinc-300"><ChevronRight className="h-4 w-4" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DrillModal({ rep, data, onClose }) {
  const navigate = useNavigate();
  const rows = data.activities.filter((a) => a.rep_id === rep.rep_id);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F172A]/60 backdrop-blur-sm" data-testid="activity-drill-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="bg-white border border-zinc-200 w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-5 bg-[#0F172A] text-white">
          <h3 className="font-display font-bold">{rep.rep_name} · {rows.length} actions</h3>
          <button onClick={onClose} data-testid="activity-drill-close"><X className="h-5 w-5 text-zinc-300 hover:text-white" /></button>
        </div>
        <div className="overflow-y-auto divide-y divide-zinc-100">
          {rows.map((a) => (
            <div key={a.id} className="p-4 flex items-start gap-3" data-testid={`activity-drill-row-${a.id}`}>
              <span className="text-xs font-mono text-zinc-400 w-24 shrink-0">{a.call_date}</span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={() => navigate(`/crm/customers/${a.customer_id}`)} className="font-medium text-[#0F172A] hover:underline">{a.company_name || "Customer"}</button>
                  <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 border" style={{ borderColor: TYPE_COLORS[a.event_type], color: TYPE_COLORS[a.event_type] }}>{a.event_type}</span>
                </div>
                {(a.contact || a.outcome) && <p className="text-xs text-zinc-500 mt-0.5">{[a.contact, a.outcome].filter(Boolean).join(" · ")}</p>}
                {a.notes && <p className="text-sm text-zinc-600 mt-0.5">{a.notes}</p>}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
